"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import Categories, { brandColors } from "../data/techStack";
import { useServiceData } from "../hooks/useServiceData";

const BRAND = "#F48428";

export default function TechStackTabs(props) {
  const { data: backendPageData } = useServiceData();
  const [active, setActive] = useState("");
  const [techData, setTechData] = useState(null);

  useEffect(() => {
    let rawSource = null;

    // 1. Priority: Prop-passed data
    if (props.dynamicData) {
      rawSource = props.dynamicData;
    }
    // 2. Secondary: Backend data from hook
    else if (backendPageData) {
      // Prioritize the 'technologies' field found in the API response
      rawSource = backendPageData.technologies;

      // Fallback: Check extraSections for tech stack if technologies field is empty/missing
      if (!rawSource || (!rawSource.groups && !rawSource.categories)) {
        const extraSection = backendPageData.extraSections?.find(
          (s) => s.type === "techStackSection" || s.type === "TechStack"
        );
        if (extraSection?.refId) {
          rawSource = extraSection.refId;
        }
      }
    }

    if (rawSource) {
      // Find the groups array (could be 'groups', 'categories', 'cards', or 'items')
      const rawGroups = rawSource.groups || rawSource.categories || rawSource.cards || rawSource.items;

      const normalized = {
        title: rawSource.title,
        subtitle: rawSource.subtitle,
        categories: (Array.isArray(rawGroups) ? rawGroups : []).map((g, idx) => {
          const rawItems = g.items || g.cards || g.techs || g.data || [];
          return {
            key: (g.groupTitle || g.title || g.name || `cat-${idx}`).toLowerCase().replace(/\s+/g, "-"),
            label: g.groupTitle || g.title || g.name || `Category ${idx + 1}`,
            items: (Array.isArray(rawItems) ? rawItems : []).map(it => ({
              name: it.name || it.label || it.title,
              icon: it.icon
            }))
          };
        })
      };

      setTechData(normalized);

      // Initialize active tab if not set or not in current data
      if (normalized.categories.length > 0) {
        const isValidActive = normalized.categories.some(c => c.key === active);
        if (!active || !isValidActive) {
          setActive(normalized.categories[0].key);
        }
      }
    }
  }, [props.dynamicData, backendPageData]);

  const displayCategories = techData?.categories || Categories;
  const current = displayCategories.find((c) => c.key === active) || displayCategories[0];

  const tabBtn = (isActive) => ({
    padding: "10px 16px",
    borderRadius: 10,
    border: `1px solid ${isActive ? "#000" : "rgba(255,255,255,0.15)"}`,
    background: isActive ? "#0e0e0e" : "rgba(255,255,255,0.06)",
    color: isActive ? "#fff" : "#e5e5e5",
    fontWeight: 700,
    cursor: "pointer",
  });

  const chip = {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    borderRadius: 12,
    background: "#fff",
    color: "#111",
    padding: "10px 12px",
    border: "1px solid rgba(0,0,0,0.06)",
    boxShadow: "0 6px 14px rgba(0,0,0,0.08)",
    fontWeight: 600,
  };

  const getImageUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    if (url.startsWith("data:") || url.startsWith("blob:")) return url;
    
    const apiUrl = (process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_API_BASE_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || "";
    
    if (url.includes("/uploads/")) {
      const cleanPath = url.substring(url.indexOf("/uploads/"));
      return `${apiUrl}${cleanPath}`;
    }
    
    if (url.startsWith("uploads/") || url.startsWith("/uploads/")) {
      const cleanPath = url.startsWith("/") ? url : `/${url}`;
      return `${apiUrl}${cleanPath}`;
    }
    
    if (url.startsWith("http")) return url;
    return url;
  };

  const getIconUrl = (icon) => {
    if (!icon) return "";
    if (typeof icon === "string") return icon;
    return icon.url || "";
  };

  if (!current) return null;

  return (
    <section className="py-5">
      <div className="container text-center">
        <div className="text-uppercase small mb-2" style={{ color: BRAND, letterSpacing: ".18em" }}>
          {techData?.title || "Other Technologies We Work On"}
        </div>
        <h2 className="fw-bold text-white">
          {techData?.subtitle || "Tailored Technologies to Conquer Your Development Challenges"}
        </h2>
        <p className=" mb-4">Harness the power of our advanced technologies to elevate user interaction and drive engagement.</p>

        <div className="d-flex flex-wrap justify-content-center gap-2 mb-4">
          {displayCategories.map((c) => {
            const a = c.key === active;
            return (
              <button key={c.key} type="button" onClick={() => setActive(c.key)} style={tabBtn(a)}>
                {c.label}
              </button>
            );
          })}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center" }}>
          {current.items.map((it, idx) => {
            const isWide = it.name.length > 14;
            const boxStyle = { ...chip, minWidth: isWide ? 220 : 140, padding: isWide ? "12px 18px" : chip.padding, lineHeight: 1.15, textAlign: "left" };
            const color = brandColors[it.name] || BRAND;
            const iconUrl = getIconUrl(it.icon);
            const isImage = typeof iconUrl === "string" && (iconUrl.startsWith("http") || iconUrl.includes("/") || iconUrl.includes("."));
            return (
              <div key={`${current.key}-${it.name}-${idx}`} style={boxStyle}>
                {isImage ? (
                  <Image src={getImageUrl(iconUrl)} alt={it.name} style={{ objectFit: "contain" }} width={20} height={20} />
                ) : (
                  <i className={iconUrl} style={{ color, fontSize: 20, minWidth: 18, textAlign: 'center' }}></i>
                )}
                <span style={{ whiteSpace: "normal" }}>{it.name}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
